// src/components/Dashboard/DirectDataIntegration.js
import React, { useState, useEffect, useRef } from 'react';
import { Card, Form, Button, Table, Tabs, Tab, Alert, Spinner, Modal, InputGroup } from 'react-bootstrap';
import { v4 as uuidv4 } from 'uuid';

import logger from '../../utils/logger';

/**
 * DirectDataIntegration - Component to allow users to directly edit financial data
 * without requiring Plaid integration - Updated with modern UI matching PlaidDataSidebar
 */
const DirectDataIntegration = ({
	isVisible,
	onDataChange,
	initialData = null,
	userId,
	currentIntegrationMode = 'direct'
}) => {
	// Ref to track initialization
	const isInitialized = useRef(false);

	// Default sample data structure
	const defaultData = {
		accounts: [
			{
				id: 'acc_' + uuidv4().substring(0, 8),
				name: 'Checking Account',
				type: 'depository',
				subtype: 'checking',
				balances: {
					available: 2500.75,
					current: 2500.75,
					limit: null,
					isoCurrencyCode: 'USD',
				},
				mask: '1234'
			},
			{
				id: 'acc_' + uuidv4().substring(0, 8),
				name: 'Savings Account',
				type: 'depository',
				subtype: 'savings',
				balances: {
					available: 12750.32,
					current: 12750.32,
					limit: null,
					isoCurrencyCode: 'USD',
				},
				mask: '5678'
			},
			{
				id: 'acc_' + uuidv4().substring(0, 8),
				name: 'Credit Card',
				type: 'credit',
				subtype: 'credit card',
				balances: {
					available: 3200.00,
					current: 1800.00,
					limit: 5000.00,
					isoCurrencyCode: 'USD',
				},
				mask: '9012'
			}
		],
		transactions: [
			{
				id: 'txn_' + uuidv4().substring(0, 8),
				account_id: '', // Will be set to match first account
				amount: -32.45,
				category: ['Food and Drink', 'Restaurants'],
				date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
				name: 'Restaurant ABC',
				payment_channel: 'in store',
				pending: false
			},
			{
				id: 'txn_' + uuidv4().substring(0, 8),
				account_id: '', // Will be set to match first account
				amount: 1500.00,
				category: ['Transfer', 'Deposit'],
				date: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
				name: 'PAYROLL DEPOSIT',
				payment_channel: 'other',
				pending: false
			},
			{
				id: 'txn_' + uuidv4().substring(0, 8),
				account_id: '', // Will be set to match first account
				amount: -82.12,
				category: ['Shops', 'Grocery'],
				date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
				name: 'Grocery Store XYZ',
				payment_channel: 'in store',
				pending: false
			},
			{
				id: 'txn_' + uuidv4().substring(0, 8),
				account_id: '', // Will be set to match second account
				amount: -500.00,
				category: ['Transfer', 'Withdrawal'],
				date: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
				name: 'Transfer to Savings',
				payment_channel: 'other',
				pending: false
			},
			{
				id: 'txn_' + uuidv4().substring(0, 8),
				account_id: '', // Will be set to match third account
				amount: -125.30,
				category: ['Shops', 'Online Shopping'],
				date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
				name: 'AMAZON.COM',
				payment_channel: 'online',
				pending: false
			}
		],
		institution: 'Demo Bank',
		lastUpdated: new Date().toISOString(),
		userId: userId // Store user ID with the data for validation
	};

	// State for the financial data
	const [financialData, setFinancialData] = useState(null);

	// State for the active sections
	const [activeKey, setActiveKey] = useState(['0', '1']);

	// State for edit modals
	const [showAccountModal, setShowAccountModal] = useState(false);
	const [showTransactionModal, setShowTransactionModal] = useState(false);
	const [currentAccount, setCurrentAccount] = useState(null);
	const [currentTransaction, setCurrentTransaction] = useState(null);

	// Loading state
	const [loading, setLoading] = useState(false);

	// Initialize with default data or provided initial data - ONCE ONLY
	useEffect(() => {
		// Only run this effect if data isn't initialized and component is visible
		if (!isInitialized.current && isVisible) {
			setLoading(true);
			logger.info(`Initializing direct data for user ${userId}`);
			isInitialized.current = true;

			// If initial data is provided, use it, otherwise use default
			let dataToUse;

			if (initialData) {
				dataToUse = JSON.parse(JSON.stringify(initialData)); // Deep clone
			} else {
				dataToUse = JSON.parse(JSON.stringify(defaultData)); // Deep clone default

				// Set account_id for transactions to match the accounts
				if (dataToUse.accounts.length >= 3 && dataToUse.transactions.length >= 5) {
					dataToUse.transactions[0].account_id = dataToUse.accounts[0].id;
					dataToUse.transactions[1].account_id = dataToUse.accounts[0].id;
					dataToUse.transactions[2].account_id = dataToUse.accounts[0].id;
					dataToUse.transactions[3].account_id = dataToUse.accounts[1].id;
					dataToUse.transactions[4].account_id = dataToUse.accounts[2].id;
				}
			}

			setFinancialData(dataToUse);

			// Notify parent of data change
			if (onDataChange) {
				onDataChange(dataToUse);
			}

			setLoading(false);
		}
	}, [isVisible]); // Only run when visibility changes, not on every render

	// Reset when user changes
	useEffect(() => {
		if (userId && isInitialized.current) {
			// Only reset if user ID changes AND we're already initialized
			logger.info(`User changed to ${userId}, resetting direct data`);

			// Reset initialization flag
			isInitialized.current = false;
		}
	}, [userId]);

	// Enhanced handler to update financial data and notify parent
	const updateFinancialData = (newData) => {
		// Ensure we're in direct mode before updating
		if (currentIntegrationMode !== 'direct') {
			logger.warn("Attempted to update direct data while in Plaid mode");
			return;
		}

		// Update lastUpdated timestamp
		newData.lastUpdated = new Date().toISOString();

		// Always ensure userId is set correctly
		newData.userId = userId;

		// Update the state
		setFinancialData(newData);

		// Notify parent of data change using a deep copy to prevent reference issues
		if (onDataChange) {
			onDataChange(JSON.parse(JSON.stringify(newData)));
		}
	};

	// Add a useEffect to log mode changes and ensure data isolation
	useEffect(() => {
		if (currentIntegrationMode === 'direct') {
			logger.info("DirectDataIntegration: Active in Direct mode");
		} else {
			logger.info("DirectDataIntegration: Inactive in Plaid mode");
		}
	}, [currentIntegrationMode]);

	const handleAccordionToggle = (key) => {
		if (activeKey.includes(key)) {
			setActiveKey(activeKey.filter(k => k !== key));
		} else {
			setActiveKey([...activeKey, key]);
		}
	};

	const formatCurrency = (amount) => {
		try {
			return new Intl.NumberFormat('en-US', {
				style: 'currency',
				currency: 'USD'
			}).format(Math.abs(amount));
		} catch (error) {
			logger.error('Error formatting currency:', error);
			return `$${Math.abs(amount)}`;
		}
	};

	const formatDate = (dateString) => {
		try {
			return new Date(dateString).toLocaleDateString();
		} catch (error) {
			logger.error('Error formatting date:', error);
			return dateString;
		}
	};

	const calculateTotals = () => {
		if (!financialData || !financialData.accounts || !Array.isArray(financialData.accounts)) {
			return { total: 0, assets: 0, liabilities: 0 };
		}

		return financialData.accounts.reduce((totals, account) => {
			const isCreditCard = account.type === 'credit';
			const balance = account.balances.current;

			if (isCreditCard) {
				// For credit cards, the "current" balance is what you owe
				totals.liabilities += Math.abs(balance);
				totals.total -= Math.abs(balance); // Subtract debt from net worth
			} else {
				totals.assets += balance;
				totals.total += balance;
			}

			return totals;
		}, { total: 0, assets: 0, liabilities: 0 });
	};

	// Handler for opening the account edit modal
	const handleEditAccount = (account) => {
		setCurrentAccount({ ...account });
		setShowAccountModal(true);
	};

	// Handler for opening the transaction edit modal
	const handleEditTransaction = (transaction) => {
		setCurrentTransaction({ ...transaction });
		setShowTransactionModal(true);
	};

	// Handler for saving account changes
	const handleSaveAccount = () => {
		if (!currentAccount) return;

		// Create a deep copy of the financial data
		const newData = JSON.parse(JSON.stringify(financialData));

		// If this is a new account, add it to the array
		if (!currentAccount.id) {
			currentAccount.id = 'acc_' + uuidv4().substring(0, 8);
			newData.accounts.push(currentAccount);
		} else {
			// Otherwise update the existing account
			const index = newData.accounts.findIndex(acc => acc.id === currentAccount.id);
			if (index !== -1) {
				newData.accounts[index] = currentAccount;
			}
		}

		// Update the data
		updateFinancialData(newData);

		// Close the modal
		setShowAccountModal(false);
		setCurrentAccount(null);
	};

	// Handler for saving transaction changes
	const handleSaveTransaction = () => {
		if (!currentTransaction) return;

		// Create a deep copy of the financial data
		const newData = JSON.parse(JSON.stringify(financialData));

		// If this is a new transaction, add it to the array
		if (!currentTransaction.id) {
			currentTransaction.id = 'txn_' + uuidv4().substring(0, 8);
			newData.transactions.push(currentTransaction);
		} else {
			// Otherwise update the existing transaction
			const index = newData.transactions.findIndex(txn => txn.id === currentTransaction.id);
			if (index !== -1) {
				newData.transactions[index] = currentTransaction;
			}
		}

		// Update the data
		updateFinancialData(newData);

		// Close the modal
		setShowTransactionModal(false);
		setCurrentTransaction(null);
	};

	// Handler for deleting an account
	const handleDeleteAccount = (accountId) => {
		if (window.confirm('Are you sure you want to delete this account? This will also delete all associated transactions.')) {
			// Create a deep copy of the financial data
			const newData = JSON.parse(JSON.stringify(financialData));

			// Remove the account
			newData.accounts = newData.accounts.filter(acc => acc.id !== accountId);

			// Remove associated transactions
			newData.transactions = newData.transactions.filter(txn => txn.account_id !== accountId);

			// Update the data
			updateFinancialData(newData);
		}
	};

	// Handler for deleting a transaction
	const handleDeleteTransaction = (transactionId) => {
		if (window.confirm('Are you sure you want to delete this transaction?')) {
			// Create a deep copy of the financial data
			const newData = JSON.parse(JSON.stringify(financialData));

			// Remove the transaction
			newData.transactions = newData.transactions.filter(txn => txn.id !== transactionId);

			// Update the data
			updateFinancialData(newData);
		}
	};

	// Handler for adding a new account
	const handleAddAccount = () => {
		setCurrentAccount({
			id: '',
			name: 'New Account',
			type: 'depository',
			subtype: 'checking',
			balances: {
				available: 0,
				current: 0,
				limit: null,
				isoCurrencyCode: 'USD',
			},
			mask: '0000'
		});
		setShowAccountModal(true);
	};

	// Handler for adding a new transaction
	const handleAddTransaction = () => {
		// Check if there are any accounts first
		if (!financialData?.accounts?.length) {
			alert('Please add an account first before adding transactions.');
			return;
		}

		setCurrentTransaction({
			id: '',
			account_id: financialData.accounts[0].id, // Default to first account
			amount: 0,
			category: ['Uncategorized'],
			date: new Date().toISOString().split('T')[0],
			name: 'New Transaction',
			payment_channel: 'other',
			pending: false
		});
		setShowTransactionModal(true);
	};

	// Handler for resetting to default data
	const handleResetData = () => {
		if (window.confirm('Are you sure you want to reset all data to default values? This cannot be undone.')) {
			// Reset to default data
			const resetData = JSON.parse(JSON.stringify(defaultData));

			// Set account_id for transactions to match the accounts
			if (resetData.accounts.length >= 3 && resetData.transactions.length >= 5) {
				resetData.transactions[0].account_id = resetData.accounts[0].id;
				resetData.transactions[1].account_id = resetData.accounts[0].id;
				resetData.transactions[2].account_id = resetData.accounts[0].id;
				resetData.transactions[3].account_id = resetData.accounts[1].id;
				resetData.transactions[4].account_id = resetData.accounts[2].id;
			}

			// Update data
			updateFinancialData(resetData);
		}
	};

	// Render account edit modal
	const renderAccountModal = () => (
		<Modal show={showAccountModal} onHide={() => setShowAccountModal(false)}>
			<Modal.Header closeButton>
				<Modal.Title>{currentAccount?.id ? 'Edit Account' : 'Add Account'}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<Form>
					<Form.Group className="mb-3">
						<Form.Label>Account Name</Form.Label>
						<Form.Control
							type="text"
							value={currentAccount?.name || ''}
							onChange={(e) => setCurrentAccount({ ...currentAccount, name: e.target.value })}
						/>
					</Form.Group>

					<Form.Group className="mb-3">
						<Form.Label>Account Type</Form.Label>
						<Form.Select
							value={currentAccount?.type || 'depository'}
							onChange={(e) => setCurrentAccount({ ...currentAccount, type: e.target.value })}
						>
							<option value="depository">Depository</option>
							<option value="credit">Credit</option>
							<option value="loan">Loan</option>
							<option value="investment">Investment</option>
							<option value="other">Other</option>
						</Form.Select>
					</Form.Group>

					<Form.Group className="mb-3">
						<Form.Label>Account Subtype</Form.Label>
						<Form.Control
							type="text"
							value={currentAccount?.subtype || ''}
							onChange={(e) => setCurrentAccount({ ...currentAccount, subtype: e.target.value })}
							placeholder="checking, savings, credit card, etc."
						/>
					</Form.Group>

					<Form.Group className="mb-3">
						<Form.Label>Current Balance</Form.Label>
						<InputGroup>
							<InputGroup.Text>$</InputGroup.Text>
							<Form.Control
								type="number"
								step="0.01"
								value={currentAccount?.balances?.current || 0}
								onChange={(e) => setCurrentAccount({
									...currentAccount,
									balances: {
										...currentAccount.balances,
										current: parseFloat(e.target.value) || 0,
										available: currentAccount.type === 'credit' ?
											(currentAccount.balances.limit || 0) - parseFloat(e.target.value) || 0 :
											parseFloat(e.target.value) || 0
									}
								})}
							/>
						</InputGroup>
					</Form.Group>

					{currentAccount?.type === 'credit' && (
						<Form.Group className="mb-3">
							<Form.Label>Credit Limit</Form.Label>
							<InputGroup>
								<InputGroup.Text>$</InputGroup.Text>
								<Form.Control
									type="number"
									step="0.01"
									value={currentAccount?.balances?.limit || 0}
									onChange={(e) => setCurrentAccount({
										...currentAccount,
										balances: {
											...currentAccount.balances,
											limit: parseFloat(e.target.value) || 0,
											available: parseFloat(e.target.value) - (currentAccount.balances.current || 0) || 0
										}
									})}
								/>
							</InputGroup>
						</Form.Group>
					)}

					<Form.Group className="mb-3">
						<Form.Label>Last 4 Digits</Form.Label>
						<Form.Control
							type="text"
							maxLength="4"
							value={currentAccount?.mask || ''}
							onChange={(e) => {
								const mask = e.target.value.replace(/[^0-9]/g, '').substring(0, 4);
								setCurrentAccount({ ...currentAccount, mask });
							}}
							placeholder="1234"
						/>
					</Form.Group>
				</Form>
			</Modal.Body>
			<Modal.Footer>
				<button className="modern-btn modern-btn-secondary" onClick={() => setShowAccountModal(false)}>
					Cancel
				</button>
				<button className="modern-btn modern-btn-primary" onClick={handleSaveAccount}>
					Save
				</button>
			</Modal.Footer>
		</Modal>
	);

	// Render transaction edit modal
	const renderTransactionModal = () => (
		<Modal show={showTransactionModal} onHide={() => setShowTransactionModal(false)}>
			<Modal.Header closeButton>
				<Modal.Title>{currentTransaction?.id ? 'Edit Transaction' : 'Add Transaction'}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<Form>
					<Form.Group className="mb-3">
						<Form.Label>Account</Form.Label>
						<Form.Select
							value={currentTransaction?.account_id || ''}
							onChange={(e) => setCurrentTransaction({ ...currentTransaction, account_id: e.target.value })}
						>
							{financialData?.accounts.map(account => (
								<option key={account.id} value={account.id}>
									{account.name} (****{account.mask})
								</option>
							))}
						</Form.Select>
					</Form.Group>

					<Form.Group className="mb-3">
						<Form.Label>Transaction Name/Description</Form.Label>
						<Form.Control
							type="text"
							value={currentTransaction?.name || ''}
							onChange={(e) => setCurrentTransaction({ ...currentTransaction, name: e.target.value })}
						/>
					</Form.Group>

					<Form.Group className="mb-3">
						<Form.Label>Amount (negative for expenses, positive for income)</Form.Label>
						<InputGroup>
							<InputGroup.Text>$</InputGroup.Text>
							<Form.Control
								type="number"
								step="0.01"
								value={currentTransaction?.amount || 0}
								onChange={(e) => setCurrentTransaction({ ...currentTransaction, amount: parseFloat(e.target.value) || 0 })}
							/>
						</InputGroup>
						<Form.Text className="text-muted">
							Use negative values for expenses (e.g., -50.25) and positive values for income/deposits (e.g., 1000.00)
						</Form.Text>
					</Form.Group>

					<Form.Group className="mb-3">
						<Form.Label>Date</Form.Label>
						<Form.Control
							type="date"
							value={currentTransaction?.date || new Date().toISOString().split('T')[0]}
							onChange={(e) => setCurrentTransaction({ ...currentTransaction, date: e.target.value })}
						/>
					</Form.Group>

					<Form.Group className="mb-3">
						<Form.Label>Category</Form.Label>
						<Form.Control
							type="text"
							value={currentTransaction?.category?.join(', ') || ''}
							onChange={(e) => setCurrentTransaction({
								...currentTransaction,
								category: e.target.value.split(',').map(cat => cat.trim()).filter(cat => cat)
							})}
							placeholder="e.g. Food and Drink, Restaurants"
						/>
						<Form.Text className="text-muted">
							Separate multiple categories with commas
						</Form.Text>
					</Form.Group>

					<Form.Group className="mb-3">
						<Form.Label>Payment Channel</Form.Label>
						<Form.Select
							value={currentTransaction?.payment_channel || 'other'}
							onChange={(e) => setCurrentTransaction({ ...currentTransaction, payment_channel: e.target.value })}
						>
							<option value="online">Online</option>
							<option value="in store">In Store</option>
							<option value="other">Other</option>
						</Form.Select>
					</Form.Group>

					<Form.Group className="mb-3">
						<Form.Check
							type="checkbox"
							label="Pending"
							checked={currentTransaction?.pending || false}
							onChange={(e) => setCurrentTransaction({ ...currentTransaction, pending: e.target.checked })}
						/>
					</Form.Group>
				</Form>
			</Modal.Body>
			<Modal.Footer>
				<button className="modern-btn modern-btn-secondary" onClick={() => setShowTransactionModal(false)}>
					Cancel
				</button>
				<button className="modern-btn modern-btn-primary" onClick={handleSaveTransaction}>
					Save
				</button>
			</Modal.Footer>
		</Modal>
	);

	// If the component isn't visible or data is loading, don't render
	if (!isVisible || loading || !financialData) {
		return isVisible ? (
			<div className="modern-loading-container">
				<div className="modern-spinner">
					<div className="spinner-ring"></div>
					<div className="spinner-ring"></div>
					<div className="spinner-ring"></div>
				</div>
				<p className="loading-text">Loading your simulated financial data...</p>
			</div>
		) : null;
	}

	const totals = calculateTotals();

	return (
		<div className="modern-plaid-data-sidebar">
			{/* Fixed Header - Non-scrollable */}
			<div className="modern-sidebar-header">
				<div className="header-content">
					<div className="header-icon">
						<i className="bi bi-database"></i>
					</div>
					<div className="header-text">
						<h5>Simulated Financial Data</h5>
						<span className="header-subtitle">Demo Bank - Editable Test Data</span>
					</div>
				</div>
				<div className="connection-badge" style={{
					background: 'rgba(255, 193, 7, 0.15)',
					borderColor: 'rgba(255, 193, 7, 0.3)',
					color: '#ffc107'
				}}>
					<div className="connection-indicator" style={{
						background: '#ffc107'
					}}></div>
					<span>Demo</span>
				</div>
			</div>

			{/* Scrollable Body Content */}
			<div className="modern-sidebar-body">
				{/* User validation warning */}
				{financialData.userId && userId && financialData.userId !== userId && (
					<div className="modern-alert error">
						<div className="alert-icon">
							<i className="bi bi-exclamation-triangle-fill"></i>
						</div>
						<div className="alert-content">
							<strong>Security Alert:</strong> Data ownership mismatch detected
						</div>
						<button className="modern-btn modern-btn-xs modern-btn-danger alert-action" onClick={handleResetData}>
							Reset Data
						</button>
					</div>
				)}

				{/* Info Alert */}
				<div className="modern-alert warning">
					<div className="alert-icon">
						<i className="bi bi-info-circle-fill"></i>
					</div>
					<div className="alert-content">
						This is simulated financial data for testing. Changes persist during your session only.
					</div>
				</div>

				{/* Modern Financial Summary */}
				<div className="financial-summary-card" style={{
					background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.1) 0%, rgba(255, 193, 7, 0.05) 100%)',
					borderColor: 'rgba(255, 193, 7, 0.2)'
				}}>
					<div className="summary-header">
						<h6>Financial Overview</h6>
						<div className="summary-icon" style={{
							background: 'linear-gradient(45deg, #ffc107, #ffdb4d)'
						}}>
							<i className="bi bi-graph-up"></i>
						</div>
					</div>

					<div className="summary-metrics">
						<div className="metric-item">
							<span className="metric-label">Total Assets</span>
							<span className="metric-value positive">
								{formatCurrency(totals.assets)}
							</span>
						</div>

						<div className="metric-item">
							<span className="metric-label">Total Liabilities</span>
							<span className="metric-value negative">
								{formatCurrency(totals.liabilities)}
							</span>
						</div>

						<div className="metric-divider"></div>

						<div className="metric-item total">
							<span className="metric-label">Net Worth</span>
							<span className={`metric-value ${totals.total >= 0 ? 'positive' : 'negative'}`}>
								{formatCurrency(totals.total)}
							</span>
						</div>
					</div>

					<div className="summary-footer">
						<span className="last-updated">
							Updated {formatDate(financialData.lastUpdated || new Date())}
						</span>
					</div>
				</div>

				{/* Modern Expandable Sections */}
				<div className="modern-expandable-sections">
					{/* Accounts Section */}
					<div className="expandable-section">
						<button
							className={`section-header ${activeKey.includes('0') ? 'expanded' : ''}`}
							onClick={() => handleAccordionToggle('0')}
						>
							<div className="section-title">
								<div className="section-icon">
									<i className="bi bi-wallet2"></i>
								</div>
								<span>Accounts</span>
								<div className="section-count">
									{financialData.accounts?.length || 0}
								</div>
							</div>
							<div className="expand-icon">
								<i className={`bi bi-chevron-${activeKey.includes('0') ? 'up' : 'down'}`}></i>
							</div>
						</button>

						{activeKey.includes('0') && (
							<div className="section-content">
								<div className="modern-actions" style={{ marginBottom: '1rem' }}>
									<button
										className="modern-btn modern-btn-primary modern-btn-sm modern-btn-full"
										onClick={handleAddAccount}
									>
										<i className="bi bi-plus-circle"></i>
										Add Account
									</button>
								</div>

								<div className="accounts-grid">
									{financialData.accounts?.map(account => (
										<div key={account.id} className="account-item">
											<div>
												<div className="account-header">
													<h6 className="account-name">{account.name}</h6>
													<span className="account-type">
														{account.type === 'credit' ? 'Credit Card' :
															account.type.charAt(0).toUpperCase() + account.type.slice(1)}
														{account.subtype ? ` (${account.subtype})` : ''}
													</span>
												</div>
												<div className={`account-balance ${account.balances.current < 0 ? 'negative' : 'positive'}`}>
													{formatCurrency(account.balances.current)}
													{account.type === 'credit' && account.balances.limit && (
														<span className="balance-note">
															/ {formatCurrency(account.balances.limit)}
														</span>
													)}
												</div>
											</div>
											<div style={{ display: 'flex', gap: '0.5rem' }}>
												<button
													className="modern-btn modern-btn-ghost modern-btn-xs modern-btn-icon-only"
													onClick={() => handleEditAccount(account)}
													title="Edit Account"
												>
													<i className="bi bi-pencil"></i>
												</button>
												<button
													className="modern-btn modern-btn-danger modern-btn-xs modern-btn-icon-only"
													onClick={() => handleDeleteAccount(account.id)}
													title="Delete Account"
												>
													<i className="bi bi-trash"></i>
												</button>
											</div>
										</div>
									))}
								</div>
							</div>
						)}
					</div>

					{/* Transactions Section */}
					<div className="expandable-section">
						<button
							className={`section-header ${activeKey.includes('1') ? 'expanded' : ''}`}
							onClick={() => handleAccordionToggle('1')}
						>
							<div className="section-title">
								<div className="section-icon">
									<i className="bi bi-receipt"></i>
								</div>
								<span>Recent Transactions</span>
								<div className="section-count">
									{financialData.transactions?.length || 0}
								</div>
							</div>
							<div className="expand-icon">
								<i className={`bi bi-chevron-${activeKey.includes('1') ? 'up' : 'down'}`}></i>
							</div>
						</button>

						{activeKey.includes('1') && (
							<div className="section-content">
								<div className="modern-actions" style={{ marginBottom: '1rem' }}>
									<button
										className="modern-btn modern-btn-primary modern-btn-sm modern-btn-full"
										onClick={handleAddTransaction}
										disabled={financialData.accounts.length === 0}
									>
										<i className="bi bi-plus-circle"></i>
										Add Transaction
									</button>
								</div>

								<div className="transactions-list-container">
									<div className="transactions-list">
										{financialData.transactions?.sort((a, b) => new Date(b.date) - new Date(a.date)).map(transaction => {
											const account = financialData.accounts.find(acc => acc.id === transaction.account_id);
											return (
												<div key={transaction.id} className="transaction-item">
													<div className="transaction-info">
														<div className="transaction-header">
															<span className="transaction-date">
																{formatDate(transaction.date)}
															</span>
															<span className={`transaction-amount ${transaction.amount < 0 ? 'negative' : 'positive'}`}>
																{formatCurrency(transaction.amount)}
															</span>
														</div>
														<h6 className="transaction-description">
															{transaction.name}
															{transaction.pending && (
																<span className="badge bg-warning text-dark ms-1" style={{ fontSize: '0.6rem' }}>
																	Pending
																</span>
															)}
														</h6>
														<div className="transaction-meta">
															<span className="transaction-category">
																{Array.isArray(transaction.category) ?
																	transaction.category[0] || 'Uncategorized' :
																	transaction.category || 'Uncategorized'}
															</span>
															<span className="transaction-type">
																{account ? account.name : 'Unknown Account'}
															</span>
														</div>
													</div>
													<div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', alignItems: 'center' }}>
														<button
															className="modern-btn modern-btn-ghost modern-btn-xs modern-btn-icon-only"
															onClick={() => handleEditTransaction(transaction)}
															title="Edit Transaction"
														>
															<i className="bi bi-pencil"></i>
														</button>
														<button
															className="modern-btn modern-btn-danger modern-btn-xs modern-btn-icon-only"
															onClick={() => handleDeleteTransaction(transaction.id)}
															title="Delete Transaction"
														>
															<i className="bi bi-trash"></i>
														</button>
													</div>
												</div>
											);
										})}
									</div>
								</div>
							</div>
						)}
					</div>
				</div>
			</div>

			{/* Fixed Footer - Non-scrollable */}
			<div className="modern-sidebar-footer">
				{/* Modern Action Buttons */}
				<div className="modern-actions">
					<button
						className="modern-btn modern-btn-warning modern-btn-full"
						onClick={handleResetData}
					>
						<i className="bi bi-arrow-clockwise"></i>
						Reset to Default
					</button>
				</div>

				<div className="security-badge">
					<i className="bi bi-info-circle"></i>
					<span>
						Demo Mode: Changes are temporary
					</span>
				</div>
			</div>

			{/* Render modals */}
			{renderAccountModal()}
			{renderTransactionModal()}
		</div>
	);
};

export default DirectDataIntegration;